import { Component, inject, OnInit, OnChanges, OnDestroy, HostListener, Input, Output, EventEmitter, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Firestore, collection, collectionData, query, orderBy, where } from '@angular/fire/firestore';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-upload-history',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './upload-history.component.html',
  styleUrls: ['./upload-history.component.css']
})
export class UploadHistoryComponent implements OnInit, OnChanges, OnDestroy {
  private firestore = inject(Firestore);
  private cdr = inject(ChangeDetectorRef);
  
  private historySubscription: Subscription | undefined;
  @Input() collectionName: string = 'invoice_upload_history';
  @Input() period: string = '';
  @Output() delete = new EventEmitter<string>();
  @Output() deleteFile = new EventEmitter<{ historyId: string, importId: string, fileName: string, period: string, downloadURL?: string }>();

  allHistory: any[] = [];
  uniquePeriods: any[] = [];
  isExpanded = false;

  // Modal & Pagination
  isModalOpen = false;
  selectedPeriod = '';
  modalHistory: any[] = [];
  paginatedModalHistory: any[] = [];
  currentPage = 1;
  pageSize = 10;
  totalPages = 1;

  constructor() {}

  ngOnInit() {
    this.loadHistory();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['collectionName'] || changes['period']) {
      this.loadHistory();
    }
  }

  private loadHistory() {
    if (this.historySubscription) {
      this.historySubscription.unsubscribe();
    }

    const col = collection(this.firestore, this.collectionName);
    let q = query(col, orderBy('uploadedAt', 'desc'));

    if (this.period) {
      q = query(col, where('period', '==', this.period), orderBy('uploadedAt', 'desc'));
    }
    
    this.historySubscription = collectionData(q, { idField: 'id' }).subscribe(data => {
      // Create a copy and sort by period desc, then uploadedAt desc
      const sortedData = [...data].sort((a, b) => {
        const periodA = a['period'] || '';
        const periodB = b['period'] || '';

        // Robust sort: Parse YYYY/MM to handle unpadded months (e.g. 2024/1 vs 2024/10)
        const partsA = periodA.split('/');
        const partsB = periodB.split('/');

        if (partsA.length === 2 && partsB.length === 2) {
          const valA = parseInt(partsA[0]) * 100 + parseInt(partsA[1]);
          const valB = parseInt(partsB[0]) * 100 + parseInt(partsB[1]);
          if (!isNaN(valA) && !isNaN(valB) && valA !== valB) {
            return valB - valA;
          }
        }
        
        // Fallback to string compare
        const periodDiff = periodB.localeCompare(periodA);
        return periodDiff !== 0 ? periodDiff : (b['uploadedAt'] || '').localeCompare(a['uploadedAt'] || '');
      });
      this.allHistory = sortedData;
      this.processUniquePeriods(sortedData);

      // Keep modal list in sync if modal is open
      if (this.isModalOpen && this.selectedPeriod) {
        this.modalHistory = this.allHistory.filter(h => h.period === this.selectedPeriod);
        if (this.modalHistory.length === 0) {
          this.closeModal();
        } else {
          this.calculatePagination();
        }
      }

      this.cdr.detectChanges();
    });
  }

  ngOnDestroy() {
    if (this.historySubscription) {
      this.historySubscription.unsubscribe();
    }
  }

  processUniquePeriods(data: any[]) {
    const periods = new Set();
    this.uniquePeriods = [];
    data.forEach(item => {
      if (!periods.has(item.period)) {
        periods.add(item.period);
        this.uniquePeriods.push(item);
      }
    });
  }

  get displayedPeriods() {
    return this.isExpanded ? this.uniquePeriods : this.uniquePeriods.slice(0, 3);
  }

  toggleHistory() {
    this.isExpanded = !this.isExpanded;
  }

  openDetailModal(period: string) {
    this.selectedPeriod = period;
    this.modalHistory = this.allHistory.filter(h => h.period === period);
    this.isModalOpen = true;
    this.currentPage = 1;
    this.calculatePagination();
  }

  closeModal() {
    this.isModalOpen = false;
  }

  async deleteHistory(period: string) {
    const dataType = this.collectionName.includes('profit') ? 'profit report' : 'invoice';
    if (confirm(`Are you sure you want to delete all imports for ${period}? This will remove all ${dataType} data for this period.`)) {
      this.delete.emit(period);
    }
  }

  async deleteSingleFile(item: any) {
    const fileName = item.fileName || 'this file';
    const dataType = this.collectionName.includes('profit') ? 'profit report expenses' : 'invoice data';
    if (confirm(`Are you sure you want to delete "${fileName}"? This will remove its imported ${dataType} for ${item.period}.`)) {
      this.deleteFile.emit({
        historyId: item.id,
        importId: item.importId,
        fileName: item.fileName,
        period: item.period,
        downloadURL: item.downloadURL
      });
    }
  }

  calculatePagination() {
    this.totalPages = Math.ceil(this.modalHistory.length / this.pageSize) || 1;
    const start = (this.currentPage - 1) * this.pageSize;
    this.paginatedModalHistory = this.modalHistory.slice(start, start + this.pageSize);
  }

  changePage(delta: number) {
    const newPage = this.currentPage + delta;
    if (newPage >= 1 && newPage <= this.totalPages) {
      this.currentPage = newPage;
      this.calculatePagination();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (this.isModalOpen) {
      this.closeModal();
    }
  }
}
